import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const RoutePersistence = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading } = useAuth();
  const previousPathRef = useRef(null);

  // Track navigation history for proper back button functionality
  useEffect(() => {
    if (isAuthenticated && !isLoading && location.pathname) {
      // Don't track auth pages or verify pages
      if (!location.pathname.startsWith('/auth/')) {
        const currentPath = location.pathname;
        
        // Get the navigation history from sessionStorage
        let navHistory = [];
        try {
          const stored = sessionStorage.getItem('customerNavHistory');
          navHistory = stored ? JSON.parse(stored) : [];
        } catch (e) {
          navHistory = [];
        }

        // Only add to history if this is a different page than the last one
        if (previousPathRef.current !== currentPath) {
          // If we're going back to a page that's already in history,
          // remove all pages after it (user went back)
          const existingIndex = navHistory.indexOf(currentPath);
          if (existingIndex > -1) {
            navHistory = navHistory.slice(0, existingIndex + 1);
          } else {
            // Add new page to history
            navHistory.push(currentPath);
            
            // Keep only last 20 pages to prevent unlimited growth
            if (navHistory.length > 20) {
              navHistory = navHistory.slice(-20);
            }
          }
          
          sessionStorage.setItem('customerNavHistory', JSON.stringify(navHistory));
          previousPathRef.current = currentPath;
        }

        // Also save current route to localStorage for session restoration
        localStorage.setItem('customerCurrentRoute', currentPath);
      }
    }
  }, [location.pathname, isAuthenticated, isLoading]);

  // Restore route from localStorage on authentication
  useEffect(() => {
    // Don't interfere with verify page - let it handle its own flow
    if (location.pathname === '/auth/verify') {
      return;
    }
    
    if (isAuthenticated && !isLoading) {
      const savedRoute = localStorage.getItem('customerCurrentRoute');
      
      // Only restore if we're not already on a valid route.
      // Compare pathname only — replace-navigating to the same path strips ?query.
      if (
        savedRoute &&
        savedRoute !== '/' &&
        savedRoute !== '/dashboard' &&
        !savedRoute.startsWith('/auth/') &&
        savedRoute !== location.pathname
      ) {
        navigate(savedRoute, { replace: true });
      }
    }
  }, [isAuthenticated, isLoading, navigate, location.pathname]);

  return null; // This component doesn't render anything
};

// Helper function to get the previous page from navigation history
export const getPreviousPage = (currentPath, fallbackPath = '/dashboard') => {
  try {
    const stored = sessionStorage.getItem('customerNavHistory');
    const navHistory = stored ? JSON.parse(stored) : [];
    
    // Find current page in history
    const currentIndex = navHistory.lastIndexOf(currentPath);
    
    // If we have a previous page, return it
    if (currentIndex > 0) {
      return navHistory[currentIndex - 1];
    }
    
    // Otherwise return fallback
    return fallbackPath;
  } catch (e) {
    return fallbackPath;
  }
};

// Helper function to navigate back properly
export const navigateBack = (navigate, currentPath, fallbackPath = '/dashboard') => {
  const previousPage = getPreviousPage(currentPath, fallbackPath);
  navigate(previousPage);
};

export default RoutePersistence;
